import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { inventoryApi, vendorApi } from '@/lib/api';
import { toast } from 'sonner';
import { Search, Edit, AlertTriangle } from 'lucide-react';

export default function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    quantity: '',
    reorder_level: '',
  });

  const fetchData = async () => {
    try {
      const [inventoryRes, vendorsRes] = await Promise.all([
        inventoryApi.getAll(),
        vendorApi.getAll()
      ]);
      setInventory(inventoryRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Data fetch runs once on mount
  }, []);

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.name || '';
  };

  const getStockPercentage = (quantity, reorderLevel) => {
    if (reorderLevel === 0) return 100;
    const percentage = (quantity / (reorderLevel * 2)) * 100;
    return Math.min(percentage, 100);
  };

  const getStockColor = (quantity, reorderLevel) => {
    if (quantity <= reorderLevel * 0.25) return 'red';
    if (quantity <= reorderLevel) return 'yellow';
    return 'green';
  };

  const openModal = (item) => {
    setEditingItem(item);
    setFormData({
      quantity: item.quantity.toString(),
      reorder_level: item.reorder_level.toString(),
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await inventoryApi.update(editingItem.product_id, {
        quantity: parseInt(formData.quantity),
        reorder_level: parseInt(formData.reorder_level),
      });
      toast.success('Inventory updated successfully');
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating inventory:', error);
      toast.error('Failed to update inventory');
    }
  };

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = 
      (item.product_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (item.product_sku?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesLowStock = !showLowStock || item.is_low_stock;
    return matchesSearch && matchesLowStock;
  });

  const lowStockCount = inventory.filter(i => i.is_low_stock).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track stock levels and reorder alerts
          </p>
        </div>
        {lowStockCount > 0 && (
          <Badge className="bg-red-100 text-red-800 border border-red-200 rounded-none px-3 py-1">
            <AlertTriangle className="w-4 h-4 mr-1.5" />
            {lowStockCount} items low on stock
          </Badge>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by product name or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="inventory-search"
          />
        </div>
        <Button
          variant={showLowStock ? "default" : "outline"}
          onClick={() => setShowLowStock(!showLowStock)}
          className="rounded-sm"
          data-testid="btn-filter-low-stock"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Low Stock Only
        </Button>
      </div>

      {/* Inventory Table */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          {filteredInventory.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No inventory items found</p>
            </div>
          ) : (
            <Table data-testid="inventory-table">
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="font-medium">SKU</TableHead>
                  <TableHead className="font-medium">Product</TableHead>
                  <TableHead className="font-medium">Vendor</TableHead>
                  <TableHead className="font-medium w-[200px]">Stock Level</TableHead>
                  <TableHead className="font-medium text-right">Quantity</TableHead>
                  <TableHead className="font-medium text-right">Reorder Level</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInventory.map((item) => (
                  <TableRow 
                    key={item.id} 
                    className={item.is_low_stock ? 'bg-red-50/50' : ''}
                    data-testid={`inventory-row-${item.product_id}`}
                  >
                    <TableCell className="font-mono-data text-sm">{item.product_sku}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.product_name}</span>
                        {item.is_low_stock && (
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {getVendorName(item.vendor_id)}
                    </TableCell>
                    <TableCell>
                      <div className="stock-progress">
                        <div 
                          className={`stock-progress-fill ${getStockColor(item.quantity, item.reorder_level)}`}
                          style={{ width: `${getStockPercentage(item.quantity, item.reorder_level)}%` }}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono-data font-semibold">
                      {item.quantity}
                    </TableCell>
                    <TableCell className="text-right font-mono-data text-muted-foreground">
                      {item.reorder_level}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openModal(item)}
                        data-testid={`btn-edit-inventory-${item.product_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-sm" data-testid="inventory-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Update Stock</DialogTitle>
            <DialogDescription>Adjust inventory levels for this product.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                {editingItem?.product_name} ({editingItem?.product_sku})
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Current Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="rounded-sm mt-1.5 font-mono-data"
                  required
                  data-testid="input-inventory-quantity"
                />
              </div>
              <div>
                <Label htmlFor="reorder_level">Reorder Level</Label>
                <Input
                  id="reorder_level"
                  type="number"
                  min="0"
                  value={formData.reorder_level}
                  onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })}
                  className="rounded-sm mt-1.5 font-mono-data"
                  required
                  data-testid="input-inventory-reorder"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="rounded-sm" data-testid="btn-save-inventory">
                Update Stock
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
